import {
  capitalizeFirstChar,
  getBase64FormatForImages,
  getBase64FormatForGeneratedImages,
  getDefaultStringValue,
  getFormattedDate,
  getResolutionByAspectRatio,
  isEmptyObject,
  mapRaiOutputsCheck,
  normalizeAssetType,
  normalizeBrandId,
  REQUESTED_FORMAT_TYPE,
  stepsUtilData,
} from "@/lib/utils";
import {
  Asset,
  AssetTypesDb,
  CreatedDateDB,
  DeliverableGroup,
  GtinCopyGroup,
  GtinGroup,
  ItemWithCreatedAt,
  Project,
  PROJECT_TYPE_KEYS,
  SavedProjectRAIResponse,
  VideoRaiCheck,
  ResponseData,
  ResponseDataLanguage,
  TAB_TYPES,
  TabType,
  VideoGenerationStatus,
  VideoProjectData,
  SavedWorkImage,
} from "../../types";
import { AssetType } from "@/app/(protected)/asset-generation/components/ExploreOptions/types";
import {
  ASSET_GENERATION_TYPES,
  PROJECT_TYPES,
  STAGE_NAMES,
  workflowType,
  UNNAMED_DELIVERABLE,
  FILE_TYPE,
  FILE_UPLOAD_ASSET_TYPES,
} from "@/constants/constants";
import { ACTION_TYPES, SAVED_ASSET_TYPE, SAVED_WORK_TYPES } from "../../constants";
import type {
  ModelOutputs,
  DBRaiOutputsCheck as StepFourDBRaiOutputsCheck,
  StepFourLanguageData,
} from "@/app/(protected)/concepting/components/StepFour/types";
import { ASSET_VARIANTS, AssetTypeEnum, LANGUAGE } from "@/app/(protected)/asset-generation/components/ReviewAssets/components/constants";
import { ErrorResponse, RAI_STATUS, ReferenceImage } from "@/types";
import { getBriefUploadResponse, getProjectById } from "@/app/actions/actions";
import { Deliverable, ImageDataResponse } from "@/app/(protected)/generate-lifestyle/components/StepTwo/types";
import { PdpCopyResponse, ProductPdpCopy } from "@/app/(protected)/pdp-copy/components/ProjectCopyList/types";
import { STEP_NUMBERS } from "@/app/context/ProjectProvider/constants";
import type { SetStateAction } from "react";
import { brandMarketValidationMap } from "@/service/entity/project/helpers/constants";
import { GeneratedDescription } from "@/service/entity/rai-check/rai-check.service";
import { IMAGE_INPUT_TYPE } from "@/app/(protected)/concepting/components/StepOne/constants";
import { imageTypeOptions } from "@/app/(protected)/generate-lifestyle/components/StepTwo/components/ImageTypeRadio/constants";
import { FULLY_GENERATED_IMAGE, IMAGE_GENERATED_BACKGROUND } from "@/components/ProductPlacementImage/constants";
import { hydrateBackgroundGeneration, buildHydrateBackgroundPayload } from "@/app/(protected)/background-generation/utils";
import { useVideoGenerationStore } from "@/app/(protected)/video-generation/store/useVideoGenerationStore";
import { useStepStore } from "@/app/store/useStepStore";
import { FLAG_KEYS } from "@/service/feature-flags/constants";
import { RaiFailedCompliancesProps } from "@/components/RaiErrorAccordion";
import { logger } from "@/lib/client-logger";
import { getVideoFileName, stripExtension, stripQueryAndHash } from "@/app/(protected)/video-generation/utils";
import type { SavedMedia, EditedImagesData, LeafAsset } from "../../types";
import { aspectRatioToDimensions } from "@/lib/aspectRatioUtils";

export function processEditedImageSizes(savedMedia: SavedMedia[]): Record<string, SavedWorkImage> {
  const processedSizes: Record<string, SavedWorkImage> = {};

  savedMedia
    .filter((item) => item !== null)
    .forEach((media) => {
      const { label, sizes } = media;
      Object.entries(sizes).forEach(([sizeKey, sizeData]) => {
        if (sizeData?.isHidden) {
          return;
        }
        const formattedKey = `${sizeKey}-${label}`;
        processedSizes[formattedKey] = {
          ...sizeData,
          isEdited: true,
          label: label,
        };
      });
    });

  return processedSizes;
}

export function mergeEditedImages(editedImages: EditedImagesData, images: SavedWorkImage[]): SavedWorkImage[] {
  const modifiedImages = [...images];
  Object.entries(editedImages).forEach(([imageIndex, editedData]) => {
    const index = Number.parseInt(imageIndex, 10);
    if (Number.isNaN(index) || index < 0 || index >= modifiedImages.length) {
      return;
    }
    const { savedMedia } = editedData;

    if (!savedMedia || savedMedia.length === 0) {
      return;
    }
    const processedSizes = processEditedImageSizes(savedMedia);
    modifiedImages[index] = {
      ...modifiedImages[index],
      sizes: {
        ...modifiedImages[index].sizes,
        ...processedSizes,
      },
    };
  });

  return modifiedImages;
}

export function createLeafAssets(
  images: SavedWorkImage[],
  createdAt: string,
  createdAtUnix: number,
  aspectRatio: string,
  projectOutputId: string,
): LeafAsset[] {
  return images.map((img, idx) => ({
    id: img?.jpegURL ?? `img-${idx}`,
    name: `Image ${idx + 1}`,
    createdAt,
    seconds: createdAtUnix,
    image: img,
    aspectRatio,
    projectOutputId,
  }));
}

function processEditedImagesAndCreateLeafAssets(
  editedImages: SavedMedia[],
  images: SavedWorkImage[],
  createdAt: string,
  createdAtUnix: number,
  aspectRatio: string,
  projectOutputId: string,
): LeafAsset[] {
  const hasEditedImages = editedImages && !Array.isArray(editedImages) && Object.keys(editedImages).length > 0;

  const modifiedImages = hasEditedImages ? mergeEditedImages(editedImages, images) : images;

  return createLeafAssets(modifiedImages, createdAt, createdAtUnix, aspectRatio, projectOutputId);
}

const updateBase64ImagesForLanguages = async (
  languages: string[],
  concepterStepFour: StepFourLanguageData,
  generated_outputs: StepFourLanguageData,
  dispatchErrorState: (err: ErrorResponse) => void,
  isFeatureFlagEnabled?: (flagKey: string, brandId?: string, projectType?: string) => boolean,
) => {
  try {
    // Convert English/German gsUrl image data to Base64
    const englishImageJpegURL = generated_outputs?.modelOutputs?.languages?.english?.image?.jpegURL;
    const { projectId, projectType } = generated_outputs;
    const { brandId } = concepterStepFour;
    const normalizedBrandId = brandId && normalizeBrandId(brandId);

    let jpegBase64;
    const isNewGCSURLEnabled = isFeatureFlagEnabled?.(FLAG_KEYS.ENABLE_NEW_IMAGE_STREAM_API, normalizedBrandId, projectType) ?? false;

    if (isNewGCSURLEnabled) {
      const response = await getBase64FormatForGeneratedImages(
        { jpegURL: englishImageJpegURL },
        projectId as string,
        FILE_UPLOAD_ASSET_TYPES.GENERATED_ASSETS,
      );
      jpegBase64 = response?.jpegBase64;
    } else {
      const response = await getBase64FormatForImages({ jpegURL: englishImageJpegURL });
      jpegBase64 = response?.jpegBase64;
    }

    languages?.forEach((lang) => {
      const langImageData = concepterStepFour?.modelOutputs?.languages[lang]?.image?.jpegURL;
      if (langImageData) {
        concepterStepFour.modelOutputs.languages[lang].imageData = jpegBase64;
      }
    });
  } catch (error) {
    dispatchErrorState(error as ErrorResponse);
  }
};

const handleConvertSavedImage = async (imageUrl: string) => {
  if (!imageUrl) return;

  const base64 = await getBase64FormatForImages({ pngURL: imageUrl });
  return base64.pngBase64;
};

export const prepareConceptData = async (
  selectedProject: ResponseDataLanguage,
  dispatchErrorState: (err: ErrorResponse) => void,
  isFeatureFlagEnabled?: (flagKey: string, brandId?: string, projectType?: string) => boolean,
) => {
  const { id, concept_selection, generated_outputs, generated_outputs_rai_check, language, name, productPlacementImageUrl } =
    selectedProject ?? {};

  if (stepsUtilData.stepThreeData.concepter) {
    stepsUtilData.stepThreeData.concepter.productImageOption =
      concept_selection?.productImageOption ??
      (productPlacementImageUrl
        ? { id: IMAGE_GENERATED_BACKGROUND, label: IMAGE_GENERATED_BACKGROUND }
        : { id: FULLY_GENERATED_IMAGE, label: FULLY_GENERATED_IMAGE });
  }

  const concepterStepFour = stepsUtilData.stepFourData.concepter;

  concepterStepFour!.outputId = generated_outputs?.id;

  if (selectedProject.generated_outputs_rai_check) {
    concepterStepFour!.raiOutputsCheck = mapRaiOutputsCheck(generated_outputs_rai_check);
  }

  concepterStepFour!.videoDuration = concept_selection?.scriptDuration;
  concepterStepFour!.assetIdDescription = generated_outputs?.assetIdDescription;
  concepterStepFour!.assetIdVideoScript = generated_outputs?.assetIdVideoScript;
  concepterStepFour!.language = language;
  concepterStepFour!.brandId = selectedProject.brand;

  if (selectedProject?.generated_outputs?.modelOutputs) {
    concepterStepFour!.modelOutputs = generated_outputs.modelOutputs;
    concepterStepFour!.language = language;

    const languagesToUpdate = [LANGUAGE.ENGLISH, LANGUAGE.GERMAN, LANGUAGE.BRAZILIAN_PORTUGUESE];
    await updateBase64ImagesForLanguages(
      languagesToUpdate,
      concepterStepFour!,
      generated_outputs,
      dispatchErrorState,
      isFeatureFlagEnabled,
    );
  }

  stepsUtilData.stepOneData.concepter.projectName = name;
  stepsUtilData.currentStep = 4;
  stepsUtilData.isSavedProject = true;
  stepsUtilData.projectId = id;
};

const haveSameGtins = (itemGtins: string[] = [], selectedDeliverableGtins: string[] = []) => {
  if (itemGtins.length !== selectedDeliverableGtins.length) return false;
  const sortByLocale = (a: string, b: string) => a.localeCompare(b);
  const sortedGtin = [...itemGtins].sort(sortByLocale);
  const sortedDeliverables = [...selectedDeliverableGtins].sort(sortByLocale);
  return sortedGtin.every((val, index) => val === sortedDeliverables[index]);
};

// Take the deliverable that was used in generating the outputs from Step three and add it along with the imageList response
const getImageList = async (id: string, selectedDeliverable: Deliverable) => {
  const result: ImageDataResponse = await getBriefUploadResponse(PROJECT_TYPES.PDP, id);
  const responseData = result?.data;
  if (!responseData) return [];

  const selectedGtin = responseData.find((data) => haveSameGtins(data?.gtin ?? [], selectedDeliverable.gtin ?? []));

  if (!selectedGtin) return responseData;

  const selectedDeliverableIndex = selectedGtin.deliverables.findIndex(
    (deliverable) => deliverable.imageDeliverables === selectedDeliverable.imageDeliverables,
  );

  if (selectedDeliverableIndex !== -1) {
    selectedGtin.deliverables[selectedDeliverableIndex] = selectedDeliverable;
  }
  return responseData;
};

export const prepareLifestyleDataWithoutBrief = async (dbData: ResponseData) => {
  const { id, projectImageList } = dbData ?? {};
  stepsUtilData.selectedImageGenerationType = projectImageList?.imageDeliverables
    ? { id: projectImageList?.imageDeliverables, label: projectImageList?.imageDeliverables }
    : imageTypeOptions[0];
  stepsUtilData.stepOneData.lifeStyle.projectName = dbData.name ?? "";
  stepsUtilData.currentStep = STEP_NUMBERS.TWO;
  stepsUtilData.projectId = dbData?.id ?? "";
  stepsUtilData.isSavedProject = true;
  stepsUtilData.projectType = PROJECT_TYPES.PDP;
  stepsUtilData.savedWorkCache!.rowId = id;
  stepsUtilData.stepOneData.lifeStyle.inputType = IMAGE_INPUT_TYPE.DESCRIPTION_ID;
};

export const normalizeArrayReferenceImage = (image: ReferenceImage | ReferenceImage[] | undefined): ReferenceImage[] => {
  if (Array.isArray(image)) return image;
  if (image) return [image];
  return [];
};

export const prepareLifestyleData = async (dbData: ResponseData) => {
  if (dbData.inputSource === STAGE_NAMES.ENTER_DESCRIPTION) {
    prepareLifestyleDataWithoutBrief(dbData);
  } else {
    const { id, projectImageList, brief_upload, productPlacementImageUrl, productPlacementImagePPType, productPlacementImageDocId } =
      dbData ?? {};

    const lifestyleData = projectImageList ?? brief_upload?.uploadedFileUrl;
    const modelOutputs = dbData?.generated_outputs?.modelOutputs;

    if (!lifestyleData) {
      logger.error("Invalid or incomplete data for populating lifestyle step");
      return;
    }

    const stepTwoData = stepsUtilData.stepTwoData.lifeStyle;
    stepsUtilData.stepOneData.lifeStyle.projectName = dbData.name ?? "";
    const productPlacementBase64Data = await handleConvertSavedImage(productPlacementImageUrl ?? "");

    // The deliverable that was used to generate the outputs from Step Three
    const selectedDeliverable = {
      imageNotesOrComments: lifestyleData?.imageNotesOrComments,
      imageDeliverables: lifestyleData?.imageDeliverables,
      aspectRatio: lifestyleData?.aspectRatio,
      referenceImage: normalizeArrayReferenceImage(lifestyleData?.referenceImage),
      isLogoOnImage: lifestyleData?.isLogoOnImage,
      isImageOnText: lifestyleData?.isImageOnText,
      textInput: lifestyleData?.textInput ?? "",
      gtin: lifestyleData?.gtins,
      productImageOption: productPlacementImageUrl
        ? {
            id: IMAGE_GENERATED_BACKGROUND,
            label: IMAGE_GENERATED_BACKGROUND,
          }
        : {
            id: FULLY_GENERATED_IMAGE,
            label: FULLY_GENERATED_IMAGE,
          },
      productPlacementBase64Data: productPlacementBase64Data,
      productPlacementImageUrl,
      productPlacementImagePPType,
      productPlacementImageDocId,
    };

    const gtinAndDeliverables = (await getImageList(dbData.id, selectedDeliverable)) ?? [];

    stepTwoData.uploadedExcelData.data = gtinAndDeliverables;
    stepTwoData.selectedGtins = lifestyleData?.gtins ?? [];
    stepTwoData.selectedlifeStyle = dbData.type ?? "";
    stepTwoData.generatedContent = {
      selectedBrand: normalizeBrandId(dbData.brand),
      imagePrompt: lifestyleData.promptText ?? "",
      output:
        modelOutputs?.images?.map((image: { pngURL: string; jpegURL: string; psdURL?: string }) => ({
          jpegURL: image.jpegURL,
          pngURL: image.pngURL,
          psdURL: image.psdURL,
        })) ?? [],
    };
    stepsUtilData.currentStep = STEP_NUMBERS.TWO;
    stepsUtilData.projectId = dbData?.id ?? "";
    stepsUtilData.isSavedProject = true;
    stepsUtilData.projectType = PROJECT_TYPES.PDP;
    stepsUtilData.savedWorkCache!.rowId = id;
  }
};

const getPdpCopyList = async (id: string, selectedCopy: ProductPdpCopy) => {
  const result: PdpCopyResponse = await getBriefUploadResponse(PROJECT_TYPES.PDP_COPY, id);
  if (!result?.data) return [];
  const selectedGtin = selectedCopy?.gtin?.[0] ?? selectedCopy?.gtins?.[0];
  const selectedProjectCopy = selectedCopy?.projectCopy?.[0];
  const selectedConceptId = selectedProjectCopy?.conceptId;
  const selectedCopyIndex = result.data.findIndex((item) => (item.gtin?.[0] ?? item.gtins?.[0]) === selectedGtin);
  const pdpCopyList = result.data.map((item, index) => {
    if (index !== selectedCopyIndex || !selectedProjectCopy) return item;

    return {
      ...item,
      projectCopy: item.projectCopy.map((copy) => {
        const isSelectedCopy = selectedConceptId && copy.conceptId === selectedConceptId;
        return isSelectedCopy ? { ...copy, ...selectedProjectCopy } : copy;
      }),
    };
  });

  return pdpCopyList;
};

export const prepareDataForPdpCopy = async (dbData: ResponseData) => {
  if (!dbData) {
    logger.error("Invalid data for preparing PDP Copy step");
    return;
  }
  const { projectCopyList, name, generated_outputs, id } = dbData;
  const stepTwoData = stepsUtilData.stepTwoData.pdpCopy;

  stepsUtilData.stepOneData.pdpCopy.projectName = name ?? "";

  const projectImageListData = {
    gtin: [projectCopyList?.gtin ?? projectCopyList?.gtins?.[0]],
    projectCopy: [projectCopyList],
  };

  const pdpCopyList = (await getPdpCopyList(id, projectImageListData)) ?? [];
  stepTwoData.uploadedExcelData.data = pdpCopyList;
  stepTwoData.generatedContentPayload.gtin = projectCopyList?.gtin ?? "";
  stepsUtilData.currentStep = STEP_NUMBERS.TWO;
  stepsUtilData.projectId = id ?? "";
  stepsUtilData.isSavedProject = true;
  stepsUtilData.savedWorkCache!.rowId = id;
  if (stepsUtilData?.stepTwoData?.assetId) {
    stepsUtilData.stepTwoData.assetId.assetIdDescription = generated_outputs?.assetIdDescription;
    stepsUtilData.stepTwoData.assetId.assetIdVideoScript = generated_outputs?.assetIdVideoScript;
  }
};

const handleUniqueStages = (stage: (typeof STAGE_NAMES)[keyof typeof STAGE_NAMES], selectedProject: ResponseDataLanguage) => {
  switch (stage) {
    case STAGE_NAMES.ENTER_DESCRIPTION:
      return stage;
    case STAGE_NAMES.CONCEPT_SELECTION:
      if (selectedProject.hasOwnProperty(STAGE_NAMES.CAMPAIGN_DETAILS_REVIEW)) return stage;
      return STAGE_NAMES.USE_A_CONCEPT;

    default:
      return STAGE_NAMES.USE_A_CONCEPT;
  }
};

const convertAssetTypeValue = (type: AssetTypesDb) => {
  switch (type) {
    case ASSET_GENERATION_TYPES.VISUAL_TEXT:
      return AssetTypeEnum.VisualAndText;
    case ASSET_GENERATION_TYPES.VISUAL:
      return AssetTypeEnum.Visual;
    case ASSET_GENERATION_TYPES.TEXT:
      return AssetTypeEnum.Text;
    case ASSET_GENERATION_TYPES.VIDEO_SCRIPT:
      return AssetTypeEnum.VideoScript;

    default:
      return AssetTypeEnum.Visual;
  }
};

export const formatScriptLength = (scriptLength: string) => {
  const numericScript = scriptLength.startsWith(":") ? scriptLength.slice(1) : scriptLength;
  const numericValue = Number.parseInt(numericScript, 10);
  return Number.isNaN(numericValue) ? scriptLength : `:${String(numericValue).padStart(2, "0")}`;
};

const populateAssetGenerationFromProject = (selectedProject: ResponseDataLanguage) => {
  const {
    id,
    stage,
    assetType,
    channel,
    assetSelection,
    name,
    generated_outputs,
    generated_outputs_rai_check,
    concept_selection,
    language,
    noOfOutputs,
    productPlacementImageUrl,
    version,
  } = selectedProject;
  const { media } = generated_outputs;

  stepsUtilData.isSavedProject = true;
  stepsUtilData.selectedAspectRatio = concept_selection.aspectRatio;
  stepsUtilData.version = version;

  const assetGeneration = stepsUtilData.stepOneData.assetGeneration;
  const stepFourAssetGeneration = stepsUtilData.stepFourData.assetGeneration;

  assetGeneration.scriptLength.label = formatScriptLength(assetSelection.script ?? "");

  assetGeneration.projectName = name;
  assetGeneration.assetType = convertAssetTypeValue(assetType as AssetTypesDb);
  assetGeneration.channel.label = channel;
  assetGeneration.specifications.label = assetSelection.text;
  assetGeneration.imageDimensions =
    getResolutionByAspectRatio(assetSelection.aspectRatio, REQUESTED_FORMAT_TYPE.RESOLUTION_AND_ASPECT) ?? "";
  assetGeneration.videoScriptIncludeImage = assetSelection?.isImageIncluded;
  assetGeneration.copyOutput.label = assetSelection.text;
  assetGeneration.numberOfImages = String(noOfOutputs);
  assetGeneration.productPlacementImageUrl = productPlacementImageUrl ?? "";
  assetGeneration.productImageOption =
    concept_selection?.productImageOption ??
    (productPlacementImageUrl
      ? { id: IMAGE_GENERATED_BACKGROUND, label: IMAGE_GENERATED_BACKGROUND }
      : { id: FULLY_GENERATED_IMAGE, label: FULLY_GENERATED_IMAGE });

  stepsUtilData.stage = handleUniqueStages(stage, selectedProject);
  stepsUtilData.projectId = id;

  stepFourAssetGeneration.modelOutputs = generated_outputs.modelOutputs;
  stepFourAssetGeneration.media = media;
  stepFourAssetGeneration.language = language;
  stepFourAssetGeneration.outputId = generated_outputs.id;
  stepFourAssetGeneration.raiOutputsCheck = mapRaiOutputsCheck(generated_outputs_rai_check ?? ({} as StepFourDBRaiOutputsCheck));

  stepFourAssetGeneration.assetIdDescription = generated_outputs?.assetIdDescription;
  stepFourAssetGeneration.assetIdVideoScript = generated_outputs?.assetIdVideoScript;
};

export const getVideoGenerationStatus = (videoUrl?: string): VideoGenerationStatus =>
  videoUrl ? VideoGenerationStatus.COMPLETED : VideoGenerationStatus.IN_PROGRESS;

const getBase64FromURL = async (url: string, isNewGCSURLEnabled?: boolean, projectId?: string): Promise<string> => {
  if (isNewGCSURLEnabled && projectId) {
    const { pngBase64 } =
      (await getBase64FormatForGeneratedImages({ pngURL: url }, projectId, FILE_UPLOAD_ASSET_TYPES.GENERATED_ASSETS)) ?? {};
    return pngBase64 ?? "";
  }
  const { pngBase64 } = (await getBase64FormatForImages({ pngURL: url })) ?? {};
  return pngBase64 ?? "";
};

export const normalizeReferenceImage = async (
  img?: ReferenceImage | ReferenceImage[] | string,
  isNewGCSURLEnabled?: boolean,
  projectId?: string,
): Promise<ReferenceImage | undefined> => {
  if (!img) return undefined;
  if (Array.isArray(img)) img = img[0];
  if (!img) return undefined;

  if (typeof img === "object" && "url" in img) {
    const base64 = await getBase64FromURL(img.url, isNewGCSURLEnabled, projectId);
    return {
      url: getDefaultStringValue(img.url),
      fileId: getDefaultStringValue(img.fileId),
      fileName: getDefaultStringValue(img.fileName),
      base64,
    };
  }
  if (typeof img === "string") {
    return {
      url: img,
      fileId: "",
      fileName: "",
      base64: await getBase64FromURL(img, isNewGCSURLEnabled, projectId),
    };
  }

  return undefined;
};

const isFailedCompliancesArray = (value: unknown): value is RaiFailedCompliancesProps[] => Array.isArray(value);
const getErrorText = (errors?: string[]) => (errors && errors.length > 0 ? errors.join(", ") : undefined);

export const extractErrorMessage = (raiObj?: SavedProjectRAIResponse): string | RaiFailedCompliancesProps[] | undefined => {
  if (raiObj?.status !== "FAIL") return "";

  if (isFailedCompliancesArray(raiObj.failedCompliances) && raiObj.failedCompliances.length > 0) {
    return raiObj.failedCompliances;
  }

  const errorMessages = getErrorText(raiObj.errorMessages);
  if (errorMessages) return errorMessages;

  const errorMessage = getErrorText(raiObj.errorMessage);
  if (errorMessage) return errorMessage;

  return undefined;
};

export const extractSuccessMessage = (raiObj?: SavedProjectRAIResponse): boolean => {
  if (raiObj?.status === "PASS") return true;
  return false;
};

export const prepareVideoData = async (
  selectedProject: VideoProjectData,
  isFeatureFlagEnabled?: (flagKey: string, brandId?: string, projectType?: string) => boolean,
) => {
  const videoStore = useVideoGenerationStore.getState();
  const stepStore = useStepStore.getState();

  const { id, name, resolution, aspectRatio, version, inputs, videoProjectInputs, generated_outputs, videoDuration, brand } =
    selectedProject;
  const { description = "", referenceImage, firstFrameImage, lastFrameImage, raiChecks = [] } = inputs ?? videoProjectInputs ?? {};

  const videoUrl = generated_outputs?.modelOutputs?.videos?.[0] ?? "";

  videoStore.updateVideoConfig({
    projectId: id,
    projectName: name,
    resolution,
    aspectRatio,
    duration: videoDuration,
  });
  const normalizedBrandId = brand && normalizeBrandId(brand);
  const isNewGCSURLEnabled =
    isFeatureFlagEnabled?.(FLAG_KEYS.ENABLE_NEW_IMAGE_STREAM_API, normalizedBrandId, PROJECT_TYPES.GENERATE_VIDEO) ?? false;

  const normalizedFirstFrame = await normalizeReferenceImage(firstFrameImage ?? referenceImage, isNewGCSURLEnabled, id);
  const normalizedLastFrame = await normalizeReferenceImage(lastFrameImage, isNewGCSURLEnabled, id);
  const derivedFrameChecked = !!normalizedFirstFrame?.url;

  const savedRaiCheckMap: VideoRaiCheck = raiChecks?.[0] || {};

  const cachedRaiResponse = {
    videoDescription: savedRaiCheckMap.description ?? null,
    firstFrameUrl: savedRaiCheckMap.firstFrameImage ?? null,
    lastFrameUrl: savedRaiCheckMap.lastFrameImage ?? null,
  };

  const cachedRaiErrorMap = {
    videoDescription: extractErrorMessage(savedRaiCheckMap.description),
    firstFrameUrl: extractErrorMessage(savedRaiCheckMap.firstFrameImage),
    lastFrameUrl: extractErrorMessage(savedRaiCheckMap.lastFrameImage),
  };

  const cachedRaiSuccessMap = {
    videoDescription: extractSuccessMessage(savedRaiCheckMap.description),
    firstFrameUrl: extractSuccessMessage(savedRaiCheckMap.firstFrameImage),
    lastFrameUrl: extractSuccessMessage(savedRaiCheckMap.lastFrameImage),
  };

  videoStore.updateVideoInput({
    description,
    firstFrameImage: normalizedFirstFrame,
    lastFrameImage: normalizedLastFrame,
    isFrameChecked: derivedFrameChecked,
    cachedRaiResponse,
    cachedRaiError: cachedRaiErrorMap,
    cachedRaiSuccess: cachedRaiSuccessMap,
  });

  const outputRaiResponse = selectedProject.generated_outputs_rai_check?.images;
  const status = outputRaiResponse?.[0]?.status;
  const outputRaiStatus: RAI_STATUS | null =
    status && Object.values(RAI_STATUS).includes(status as RAI_STATUS) ? (status as RAI_STATUS) : null;

  const tcOutputResponse = selectedProject.generated_outputs_rai_check?.transcript;
  const tcStatus = tcOutputResponse?.status;
  const tcOutputStatus: RAI_STATUS | null =
    tcStatus && Object.values(RAI_STATUS).includes(tcStatus as RAI_STATUS) ? (tcStatus as RAI_STATUS) : null;

  const outputRaiFailedCompliances = (outputRaiResponse?.[0]?.failedCompliances ?? []) as RaiFailedCompliancesProps[];
  const extractedTranscript = tcOutputResponse?.assetText ?? generated_outputs?.modelOutputs?.transcript ?? "";

  videoStore.updateVideoReview({
    videoUrl,
    videoFileName: videoUrl ? (videoUrl.split("/").pop()?.split(".")[0] ?? "") : "",
    version,
    generationStatus: getVideoGenerationStatus(videoUrl),
    videoRaiStatus: outputRaiStatus,
    cachedOutputRaiResponse: outputRaiResponse,
    raiFailedCompliances: outputRaiFailedCompliances,
    transcript: extractedTranscript,
    transcriptOutputRAIResponse: tcOutputResponse,
    transcriptRAIStatus: tcOutputStatus,
    transcriptRAIFailedCompliances: tcOutputResponse?.failedCompliances as RaiFailedCompliancesProps[],
    isTranscriptFailed: !extractedTranscript,
  });

  stepStore.setCurrentStep(STEP_NUMBERS.THREE);
};

export const prepareAssetsData = (selectedProject: ResponseDataLanguage) => {
  const { id, name, generated_outputs, generated_outputs_rai_check, noOfOutputs, channel, type, projectOptions, brand, version } =
    selectedProject;

  if (type.toLowerCase() === PROJECT_TYPES.BACKGROUND) {
    const AspecRatioResolution = getResolutionByAspectRatio(projectOptions.aspectRatio, REQUESTED_FORMAT_TYPE.RESOLUTION_AND_ASPECT, brand);
    const { imageGroup, originalImages, type: modelType, image } = generated_outputs.modelOutputs as unknown as ModelOutputs;
    const formattedModelOutputs: ModelOutputs = {
      type: modelType,
      image,
      imageGroup,
      originalImages: originalImages,
    };
    const isSavedProject = true;
    const hydrationPayload = buildHydrateBackgroundPayload({
      projectName: name,
      projectId: id,
      noOfImages: String(noOfOutputs),
      channel,
      modelOutputs: formattedModelOutputs,
      raiData: generated_outputs_rai_check?.images ?? [generated_outputs_rai_check?.image],
      isSavedProject,
      imageDimensions: AspecRatioResolution as string,
      brandId: brand,
      version: version as number,
    });
    hydrateBackgroundGeneration(hydrationPayload);
  } else {
    populateAssetGenerationFromProject(selectedProject);
  }
};

export const getRawTimestamp = (item: ItemWithCreatedAt): number => {
  const { createdAt } = item;
  if (!createdAt) return 0;
  if (typeof createdAt === "object" && "_seconds" in createdAt) {
    return createdAt._seconds * 1000 + (createdAt._nanoseconds || 0) / 1000000;
  }
  if (typeof createdAt === "string") {
    return new Date(createdAt).getTime();
  }
  if (createdAt instanceof Date) {
    return createdAt.getTime();
  }
  return 0;
};

export const sortByCreationDate = <T extends ItemWithCreatedAt>(items: T[]): T[] => {
  return items.sort((a, b) => getRawTimestamp(b) - getRawTimestamp(a));
};

export const getActionTypes = (projectType: PROJECT_TYPE_KEYS, isArchived: boolean, isGeneratedOutputs: boolean, videoUrl?: string) => {
  const canViewSavedAssets = !isArchived && isGeneratedOutputs;
  const canViewVideoAssets = !isArchived && videoUrl;
  switch (projectType) {
    case PROJECT_TYPES.CAMPAIGN:
      return [ACTION_TYPES.USE_TO_GENERATE_ASSETS, ACTION_TYPES.VIEW_CONCEPT];
    case PROJECT_TYPES.PDP:
      return isArchived || !isGeneratedOutputs
        ? [ACTION_TYPES.VIEW_IMAGE_LIST]
        : [ACTION_TYPES.VIEW_IMAGE_LIST, ACTION_TYPES.VIEW_SAVED_ASSETS];
    case PROJECT_TYPES.PDP_COPY:
      return isArchived || !isGeneratedOutputs
        ? [ACTION_TYPES.VIEW_COPY_LIST]
        : [ACTION_TYPES.VIEW_COPY_LIST, ACTION_TYPES.VIEW_SAVED_ASSETS];
    case PROJECT_TYPES.GENERATE_ASSETS:
    case PROJECT_TYPES.BACKGROUND:
      return isArchived ? [ACTION_TYPES.RETURN_TO_OUTPUT_SCREEN] : [ACTION_TYPES.VIEW_SAVED_ASSETS, ACTION_TYPES.RETURN_TO_OUTPUT_SCREEN];
    case PROJECT_TYPES.GENERATE_VIDEO:
      return canViewVideoAssets
        ? [ACTION_TYPES.VIEW_SAVED_ASSETS, ACTION_TYPES.RETURN_TO_OUTPUT_SCREEN]
        : [ACTION_TYPES.RETURN_TO_OUTPUT_SCREEN];

    case PROJECT_TYPES.CONTENT:
      return canViewSavedAssets
        ? [ACTION_TYPES.VIEW_SAVED_CONTENT, ACTION_TYPES.RETURN_TO_CONVERSATION]
        : [ACTION_TYPES.RETURN_TO_CONVERSATION];

    case PROJECT_TYPES.STORYBOARD:
      return canViewSavedAssets
        ? [ACTION_TYPES.VIEW_SAVED_ASSETS, ACTION_TYPES.RETURN_TO_CONVERSATION_STORYBOARD]
        : [ACTION_TYPES.RETURN_TO_CONVERSATION_STORYBOARD];
    default:
      return [];
  }
};

export const getFormattedType = (type?: string): string => {
  switch (type) {
    case PROJECT_TYPES.CAMPAIGN:
      return SAVED_WORK_TYPES.CAMPAIGN;
    case PROJECT_TYPES.LIFESTYLE:
    case PROJECT_TYPES.PDP:
      return SAVED_WORK_TYPES.PDP;
    case PROJECT_TYPES.PDP_COPY:
      return SAVED_WORK_TYPES.PDP_COPY;
    case PROJECT_TYPES.GENERATE_ASSETS:
      return SAVED_WORK_TYPES.ASSET;
    case PROJECT_TYPES.BACKGROUND:
      return SAVED_WORK_TYPES.BACKGROUND;
    case PROJECT_TYPES.GENERATE_VIDEO:
      return SAVED_WORK_TYPES.VIDEO;
    case PROJECT_TYPES.CONTENT:
      return SAVED_WORK_TYPES.CONTENT;
    case PROJECT_TYPES.STORYBOARD:
      return SAVED_WORK_TYPES.STORYBOARD;
    default:
      return type ? type.charAt(0).toUpperCase() + type.slice(1) : "Unknown Type";
  }
};

const getSortedGtinKey = (gtins: string[] | undefined): string => {
  return gtins?.toSorted((a, b) => a.localeCompare(b)).join(", ") ?? "";
};

const groupSubAssetsByGTINsAndDeliverables = (item: ResponseData): GtinGroup[] => {
  const gtinMap = new Map<string, Map<string, DeliverableGroup>>();

  item?.allProjectOutputs?.forEach((subItem) => {
    const gtins = subItem?.inputConcept?.gtins?.toSorted((a, b) => a?.localeCompare(b))?.join(", ");
    const deliverable = subItem?.inputConcept?.imageDeliverables ?? UNNAMED_DELIVERABLE;
    const createdAt = subItem?.createdAt?._seconds; // for comparison
    const images = subItem?.modelOutputs?.images ?? [];
    const editedImages = subItem?.media?.images ?? null;
    const { aspectRatio } = subItem.inputConcept;

    if (!gtinMap.has(gtins)) {
      gtinMap.set(gtins, new Map());
    }

    const deliverableMap = gtinMap.get(gtins)!;

    const leafAssets = processEditedImagesAndCreateLeafAssets(
      editedImages,
      images,
      getFormattedDate(subItem?.createdAt),
      subItem?.createdAt?._seconds,
      aspectRatio,
      subItem.id,
    );

    const newGroup: DeliverableGroup = {
      name: deliverable,
      createdAt: getFormattedDate(subItem?.createdAt),
      createdAtUnix: subItem?.createdAt?._seconds,
      subAssets: leafAssets,
    };

    const existing = deliverableMap.get(deliverable);
    // take the latest output for a certain deilverable (if patching Lifestyle 2 for the same GTIN)
    if (!existing || createdAt > existing?.createdAtUnix) {
      deliverableMap.set(deliverable, newGroup);
    }
  });

  const result: GtinGroup[] = [];

  gtinMap.forEach((deliverableMap, gtinKey) => {
    const deliverableGroups = Array.from(deliverableMap.values() as Iterable<DeliverableGroup>).sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    result.push({
      id: gtinKey,
      name: gtinKey,
      createdAt: deliverableGroups[0]?.createdAt || "",
      subAssets: deliverableGroups,
    });
  });

  return result;
};

const groupCopySubAssetsByGTINs = (item: ResponseDataLanguage): Map<string, GtinCopyGroup> => {
  const gtinMap = new Map<string, GtinCopyGroup>();

  item?.allProjectOutputs?.forEach((subItem) => {
    const gtinKey = getSortedGtinKey(subItem?.inputConcept?.gtins);
    const createdAtUnix = subItem?.createdAt?._seconds ?? 0;
    const formattedDate = getFormattedDate(subItem?.createdAt);

    const existing = gtinMap.get(gtinKey);

    if (!existing || createdAtUnix > existing?.subItem?.createdAt._seconds || 0) {
      gtinMap.set(gtinKey, {
        createdAt: formattedDate,
        subItem,
      });
    }
  });

  return gtinMap;
};

export const createGeneratedOutput = <T extends Record<string, unknown>>(
  item: ResponseDataLanguage | ResponseData,
  modelKey: keyof ModelOutputs,
  nameSuffix: string,
  additionalFields: T = {} as T,
  index?: number,
) => {
  const { assetType: itemAssetType, concept_selection } = item;
  const assetType =
    modelKey === "image" && itemAssetType === ASSET_GENERATION_TYPES.VIDEO_SCRIPT ? ASSET_GENERATION_TYPES.VISUAL : itemAssetType;

  let name = `${normalizeAssetType((assetType as AssetType) || "Unnamed Asset", (item?.assetSelection?.text as ASSET_VARIANTS) ?? "", modelKey, true)}${nameSuffix}`;
  // If index then add it to the name ("Image - 1")
  if (index != undefined) {
    name += ` - ${index + 1}`;
  }
  const modelData =
    (item as ResponseDataLanguage)?.language && (item as ResponseDataLanguage)?.generated_outputs?.modelOutputs?.languages
      ? (item as ResponseDataLanguage)?.generated_outputs?.modelOutputs?.languages?.[(item as ResponseDataLanguage)?.language]?.[modelKey]
      : (item as ResponseData)?.generated_outputs?.modelOutputs?.[modelKey];
  if (!modelData) return null;

  return {
    id: modelKey || "unknown-id",
    name,
    aspectRatio: concept_selection?.aspectRatio ?? item.projectOptions?.aspectRatio,
    createdAt: getFormattedDate(item.createdAt as string | CreatedDateDB),
    ...additionalFields,
  };
};

const groupSubAssetsByDeliverablesOnly = (item: ResponseData): DeliverableGroup[] => {
  const deliverableMap = new Map<string, DeliverableGroup>();

  item?.allProjectOutputs?.forEach((subItem) => {
    const deliverable = subItem?.inputConcept?.imageDeliverables ?? UNNAMED_DELIVERABLE;
    const createdAtUnix = subItem?.createdAt?._seconds ?? 0;
    const createdAt = getFormattedDate(subItem?.createdAt);
    const images = subItem?.modelOutputs?.images ?? [];
    const editedImages = subItem?.media?.images ?? null;
    const { aspectRatio } = subItem.inputConcept;

    const leafAssets = processEditedImagesAndCreateLeafAssets(editedImages, images, createdAt, createdAtUnix, aspectRatio, subItem.id);

    const newGroup: DeliverableGroup = {
      name: deliverable,
      createdAt,
      createdAtUnix,
      subAssets: leafAssets,
    };

    const existing = deliverableMap.get(deliverable);

    if (!existing || createdAtUnix > existing.createdAtUnix) {
      deliverableMap.set(deliverable, newGroup);
    }
  });

  return Array.from(deliverableMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const normalizeBrand = (brand?: string) => {
  let s = (brand ?? "").toLowerCase().replaceAll(/[^a-z0-9]+/g, "-");

  let start = 0,
    end = s.length - 1;
  while (start <= end && s.codePointAt(start) === 45) start++;
  while (end >= start && s.codePointAt(end) === 45) end--;
  s = s.slice(start, end + 1);

  const parts = s.split("-").filter(Boolean);
  if (parts.length >= 2) {
    const last = parts.at(-1);
    if (last && last.length > 2) parts[parts.length - 1] = last.slice(0, 2);
  }
  return parts.join("-");
};

export const shouldGroupByGTINForBrand = (brandWithLocation?: string): boolean => {
  if (!brandWithLocation) return true;
  const normalizedBrand = normalizeBrand(brandWithLocation);

  const optionalNormalized = new Set((brandMarketValidationMap.isColumnsValidationOptional ?? []).map(normalizeBrand));

  return !optionalNormalized.has(normalizedBrand);
};

const hasGeneratedContentOutput = (item: ResponseData | ResponseDataLanguage): boolean => {
  const modelOutputs = item?.generated_outputs?.modelOutputs;
  const content = modelOutputs && "content" in modelOutputs ? modelOutputs.content : undefined;

  if (typeof content === "string") {
    return content.trim().length > 0;
  }

  return content !== null && content !== undefined;
};

const getSubAssets = (type: string, item: ResponseDataLanguage | ResponseData, brandWithLocation?: string) => {
  switch (type) {
    case SAVED_WORK_TYPES.PDP: {
      const useGtinGrouping = shouldGroupByGTINForBrand(brandWithLocation);

      if (item?.inputSource === STAGE_NAMES.ENTER_DESCRIPTION) {
        const deliverableGroups = groupSubAssetsByDeliverablesOnly(item as ResponseData);
        return deliverableGroups.map(({ name, createdAt, subAssets }) => ({
          id: name,
          name: capitalizeFirstChar(name),
          createdAt,
          productType: capitalizeFirstChar(name),
          subAssets: subAssets.map(({ id, name: leafName, createdAt: leafCreatedAt, image, aspectRatio, projectOutputId }) => ({
            id,
            name: leafName,
            createdAt: leafCreatedAt,
            image,
            aspectRatio: aspectRatio,
            productType: capitalizeFirstChar(name),
            projectOutputId,
          })),
        }));
      }

      if (useGtinGrouping) {
        const grouped = groupSubAssetsByGTINsAndDeliverables(item as ResponseData);

        return grouped.map(({ id, name, createdAt, subAssets }) => ({
          id,
          name, // GTIN string
          createdAt,
          subAssets: subAssets.map(({ name: deliverableName, createdAt: deliverableCreatedAt, subAssets: deliverableSubAssets }) => ({
            id: `${id}-${deliverableName}`,
            name: deliverableName, // imageDeliverables
            createdAt: deliverableCreatedAt,
            subAssets: deliverableSubAssets, // Final images
          })),
        }));
      }

      const deliverableGroups = groupSubAssetsByDeliverablesOnly(item as ResponseData);

      return deliverableGroups.map(({ name, createdAt, subAssets }) => ({
        id: name,
        name,
        createdAt,
        subAssets: subAssets.map(({ id, name: leafName, createdAt: leafCreatedAt, image, aspectRatio }) => ({
          id,
          name: leafName,
          createdAt: leafCreatedAt,
          image,
          aspectRatio: aspectRatio,
        })),
      }));
    }

    case SAVED_WORK_TYPES.PDP_COPY: {
      const projectName = item?.name;
      const gtinMap = groupCopySubAssetsByGTINs(item as ResponseDataLanguage);

      return Array.from(gtinMap.entries()).map(([gtinKey, { createdAt, subItem }]) => {
        const raiOutputChecks = subItem?.generated_outputs_rai_check?.generatedDescription;

        const raiFailedCheck = raiOutputChecks
          ? Object.values(subItem.generated_outputs_rai_check.generatedDescription as GeneratedDescription).find(
              (check) => check?.status === RAI_STATUS.FAIL,
            )
          : null;

        return {
          id: subItem?.id,
          name: gtinKey,
          createdAt,
          subAssets: [
            {
              id: subItem?.id,
              name: `${projectName}.txt`,
              createdAt,
              projectName,
              version: subItem?.projectVersion,
              copyData: subItem?.language
                ? subItem?.modelOutputs?.languages?.[subItem?.language]
                : subItem?.modelOutputs?.languages?.[LANGUAGE.ENGLISH],
              assetIdDescription: subItem?.assetIdDescription,
              language: subItem?.language,
              ...(raiOutputChecks ? { raiStatus: raiFailedCheck?.status ?? RAI_STATUS.PASS } : {}),
            },
          ],
        };
      });
    }

    case SAVED_WORK_TYPES.VIDEO: {
      const videoItem = item as VideoProjectData;
      const videos = videoItem.generated_outputs?.modelOutputs?.videos ?? [];
      const transcript =
        videoItem.generated_outputs_rai_check?.transcript?.assetText ?? videoItem.generated_outputs?.modelOutputs?.transcript ?? null;
      const transcriptRAIStatus = videoItem.generated_outputs_rai_check?.transcript?.status;
      const transcriptRAIFailedCompliances = videoItem.generated_outputs_rai_check?.transcript?.failedCompliances ?? [];
      const videoRaiOutputs = videoItem.generated_outputs_rai_check?.images ?? [];
      const createdAt = getFormattedDate(item.createdAt as string | CreatedDateDB);
      if (!videos || videos.length === 0) return [];

      return videos.map((videoUrl, idx) => {
        const normalizedVideoUrl = stripExtension(stripQueryAndHash(videoUrl));
        const matchedRaiResult =
          videoRaiOutputs.find((response) => stripExtension(stripQueryAndHash(response.assetUrl ?? "")) === normalizedVideoUrl) ??
          videoRaiOutputs[idx];

        return {
          id: `${videoItem.id}-video-${idx + 1}`,
          name: `${SAVED_WORK_TYPES.VIDEO} ${idx + 1}`,
          createdAt,
          videoUrl,
          videoFileName: getVideoFileName(videoUrl),
          ...(matchedRaiResult?.status ? { raiStatus: matchedRaiResult.status } : {}),
          ...(matchedRaiResult?.failedCompliances
            ? { raiFailedCompliances: matchedRaiResult.failedCompliances as RaiFailedCompliancesProps[] }
            : {}),
          transcript,
          transcriptRAIStatus,
          transcriptRAIFailedCompliances,
          isTranscriptFailed: !transcript,
        };
      });
    }

    case SAVED_WORK_TYPES.CONTENT: {
      const contentItem = item as ResponseData;
      const createdAt = getFormattedDate(contentItem.createdAt as string | CreatedDateDB);

      if (!hasGeneratedContentOutput(contentItem)) return [];

      return [
        {
          id: contentItem?.id,
          name: `${contentItem?.name}.${FILE_TYPE.HTML}`,
          content: contentItem?.generated_outputs?.modelOutputs?.content ?? "",
          contentType: contentItem?.generated_outputs?.modelOutputs?.contentType ?? "",
          projectType: contentItem?.generated_outputs?.modelOutputs?.contentType ?? "",
          raiStatus: contentItem?.generated_outputs_rai_check?.content?.status,
          raiFailedCompliances: contentItem?.generated_outputs_rai_check?.content?.failedCompliances,
          assetIdContent: contentItem?.generated_outputs?.modelOutputs?.assetIdContent ?? "",
          createdAt,
        },
      ];
    }

    case SAVED_WORK_TYPES.STORYBOARD: {
      const storyboardItem = item as ResponseData;
      const scenes = storyboardItem?.generated_outputs?.modelOutputs?.scenes ?? [];

      if (!scenes.length) return [];

      const createdAt = getFormattedDate(item.createdAt as string | CreatedDateDB);
      const imageAssets: Array<Record<string, unknown>> = [];
      const captionAssets: Array<Record<string, unknown>> = [];

      scenes.forEach((scene, idx) => {
        const frameNumber = idx + 1;
        const resolution = aspectRatioToDimensions(storyboardItem.brand ?? "", SAVED_WORK_TYPES.STORYBOARD, scene.aspectRatio ?? "");

        const captionFileName =
          scene.captionVersion === 0
            ? `${resolution}_fr${frameNumber}_${scene.assetIdCaption}`
            : `${resolution}_fr${frameNumber}-${scene.captionVersion}_${scene.assetIdCaption}`;

        // Push all image sub-assets first
        if (scene.frameUrl) {
          imageAssets.push({
            id: `${scene.sceneId}`,
            name: `Frame ${frameNumber}`,
            createdAt,
            image: {
              jpegURL: scene.frameUrl,
              pngURL: scene.frameUrl,
              psdURL: scene.frameUrl,
            },
            aspectRatio: scene?.aspectRatio,
            projectName: item.name,
            version: item.version,
            totalScenes: scenes.length,
          });
        }

        // Collect caption sub-assets separately
        if (scene.caption) {
          captionAssets.push({
            id: `${scene.sceneId}-caption`,
            name: `Frame ${frameNumber} Caption`,
            createdAt,
            description: scene.caption,
            projectName: item.name,
            assetIdDescription: captionFileName,
            version: item.version,
            language: (item as ResponseDataLanguage)?.language ?? "",
            isOriginal: scene.captionVersion === 0,
            isEdited: scene.captionVersion !== 0,
          });
        }
      });

      return [...imageAssets, ...captionAssets];
    }
    default:
      return (() => {
        const responseDataOutput =
          (item as ResponseDataLanguage)?.generated_outputs?.modelOutputs?.languages?.[(item as ResponseDataLanguage)?.language] ??
          (item as ResponseData)?.generated_outputs?.modelOutputs;
        const { assetIdDescription = "", assetIdVideoScript = "" } = item.generated_outputs ?? {};
        const imageGroupData = Array.isArray(responseDataOutput?.imageGroup)
          ? responseDataOutput.imageGroup
          : (responseDataOutput?.images ?? []);
        const editedImages = (item as ResponseData)?.generated_outputs.media?.images;
        const hasEditedImages = editedImages && !Array.isArray(editedImages) && Object.keys(editedImages).length > 0;
        const finalImages = hasEditedImages ? mergeEditedImages(editedImages, imageGroupData) : imageGroupData;
        return [
          ...(finalImages.map((image, index) =>
            createGeneratedOutput(
              item,
              "image",
              "",
              {
                image: image,
                projectName: item.name,
                version: item.version,
              },
              index,
            ),
          ) ?? []),
          createGeneratedOutput(item, "description", ".txt", {
            description: responseDataOutput?.description,
            projectName: item.name,
            version: item.version,
            assetIdDescription,
            language: (item as ResponseDataLanguage)?.language ?? "",
            ...(item?.assetSelection?.text ? { textType: item?.assetSelection?.text } : {}),
          }),
          createGeneratedOutput(item, "videoScript", ".txt", {
            videoScript: responseDataOutput?.videoScript,
            projectName: item.name,
            version: item.version,
            assetIdVideoScript,
            language: (item as ResponseDataLanguage)?.language ?? "",
          }),
        ].filter(Boolean);
      })();
  }
};

const getNameBasedOnItemType = (item: ResponseData | ResponseDataLanguage): string =>
  item.type === PROJECT_TYPES.CAMPAIGN
    ? `${getDefaultStringValue(item.name)}, ${item.concept_selection?.title}`
    : getDefaultStringValue(item.name);

const hasGeneratedOutputs = (item: ResponseData | ResponseDataLanguage): boolean => {
  if (item.type === PROJECT_TYPES.CONTENT) {
    return hasGeneratedContentOutput(item);
  }
  if (item.type === PROJECT_TYPES.STORYBOARD) {
    const scenes = (item as ResponseData)?.generated_outputs?.modelOutputs?.scenes ?? [];
    return scenes.length > 0;
  }
  return !isEmptyObject(item?.generated_outputs?.modelOutputs);
};

export const normalizeSavedWorkData = (
  data: ResponseData[] | ResponseDataLanguage[],
  type: TabType,
  brandWithLocation?: string,
): Project[] | Asset[] => {
  switch (type) {
    case "projects":
    case "archive":
      return data?.map((item) => {
        const videoUrl = (item as VideoProjectData)?.generated_outputs?.modelOutputs?.videos?.[0] ?? "";
        const isGeneratedOutputs = hasGeneratedOutputs(item);
        return {
          ...item,
          id: item.id,
          displayName: getNameBasedOnItemType(item),
          type: getFormattedType(item.type),
          defaultType: item.type,
          actions: getActionTypes(item.type as PROJECT_TYPE_KEYS, item?.isArchived, isGeneratedOutputs, videoUrl),
          createdAt: getFormattedDate(item.createdAt as string | CreatedDateDB),
          isFavorite: item.isFavorite || false,
          tabType: TAB_TYPES.PROJECTS,
          isGeneratedOutputs: isGeneratedOutputs,
          videoUrl,
        };
      });

    case "assets":
      return data?.map((item) => {
        const itemType = getFormattedType(item.type);
        const isVideo = item.type === PROJECT_TYPES.GENERATE_VIDEO;
        const isStoryboard = item.type === PROJECT_TYPES.STORYBOARD;
        return {
          id: item.id ?? "unknown-id",
          name: item.name,
          createdAt: getFormattedDate(item.createdAt as string | CreatedDateDB),
          type: itemType,
          defaultType: item.type,
          tabType: "assets",
          subAssets: getSubAssets(itemType, item, brandWithLocation),
          isFullyGeneratedImage: !(
            !!item?.productPlacementImageUrl ||
            item?.concept_selection?.productImageOption?.id === IMAGE_GENERATED_BACKGROUND ||
            item?.assetType === SAVED_ASSET_TYPE.TEXT
          ),
          ...(isVideo && { isOriginal: true, projectName: getNameBasedOnItemType(item), version: item.version }),
          ...(isStoryboard && { generated_outputs_rai_check: (item as ResponseData)?.generated_outputs_rai_check }),
        } as unknown as Asset;
      }); // see the data structure for saved assets here src\app\(protected)\saved-projects\data.ts

    default:
      return [];
  }
};

const handleStepsUpload = async (
  data: ResponseDataLanguage | ResponseData,
  projectType: PROJECT_TYPE_KEYS,
  setCurrentStep: React.Dispatch<SetStateAction<number>>,
  dispatchErrorState?: (error: ErrorResponse) => void,
  isFeatureFlagEnabled?: (flagKey: string, brandId?: string, projectType?: string) => boolean,
) => {
  switch (projectType) {
    case PROJECT_TYPES.CAMPAIGN:
      await prepareConceptData(data as ResponseDataLanguage, dispatchErrorState ?? (() => {}), isFeatureFlagEnabled);
      setCurrentStep(STEP_NUMBERS.FOUR);
      break;
    case PROJECT_TYPES.GENERATE_ASSETS:
      prepareAssetsData(data as ResponseDataLanguage);
      break;
    case PROJECT_TYPES.BACKGROUND:
      prepareAssetsData(data as ResponseDataLanguage);
      setCurrentStep(STEP_NUMBERS.THREE);
      break;
    case PROJECT_TYPES.PDP:
      await prepareLifestyleData(data as ResponseData);
      setCurrentStep(STEP_NUMBERS.TWO);
      break;
    case PROJECT_TYPES.PDP_COPY:
      await prepareDataForPdpCopy(data as ResponseData);
      setCurrentStep(STEP_NUMBERS.TWO);
      break;
    case PROJECT_TYPES.GENERATE_VIDEO:
      await prepareVideoData(data as VideoProjectData, isFeatureFlagEnabled);
      setCurrentStep(STEP_NUMBERS.THREE);
      break;

    default:
      return null;
  }
};

// Fetch a project by Id and populate step data
export const getSavedProject = async (
  projectId: string,
  projectType: workflowType | PROJECT_TYPE_KEYS,
  setCurrentStep: React.Dispatch<SetStateAction<number>>,
  startLoading: () => void,
  stopLoading: () => void,
  dispatchErrorState?: (error: ErrorResponse) => void,
  isFeatureFlagEnabled?: (flagKey: string, brandId?: string, projectType?: string) => boolean,
) => {
  try {
    startLoading();
    const savedProject = await getProjectById(projectType as workflowType, projectId);

    if (savedProject?.success) {
      await handleStepsUpload(
        savedProject?.data,
        projectType as PROJECT_TYPE_KEYS,
        setCurrentStep,
        dispatchErrorState,
        isFeatureFlagEnabled,
      );
    } else {
      dispatchErrorState?.(savedProject?.error || "Failed to load project");
    }
  } catch (error) {
    dispatchErrorState?.(error as ErrorResponse);
  } finally {
    stopLoading();
  }
};
